import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RefreshCw, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ContactButtons } from "@/components/ContactButtons";
import { toast } from "sonner";

const RSTATUS = ["renewal_due", "contacted", "interested", "quote_generated", "follow_up", "renewal_booked", "renewal_completed", "not_interested", "no_response", "lost"];
const LOST_REASONS = ["Premium too high", "Renewed elsewhere", "Vehicle/asset sold", "Not interested", "No response", "Claim dispute", "Switched insurer", "Other"];

export default function Renewals() {
  const { hasPerm } = useAuth();
  const nav = useNavigate();
  const [window, setWindow] = useState("90");
  const [items, setItems] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lostFor, setLostFor] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/renewals", { params: { window: Number(window), limit: 100 } }),
      api.get("/renewals/forecast"),
    ]).then(([a, b]) => { setItems(a.data.items || []); setForecast(b.data); }).finally(() => setLoading(false));
  };
  useEffect(load, [window]);

  const updateStatus = async (pol, rstatus, lost_reason) => {
    try { await api.put(`/renewals/${pol.id}/status`, { renewal_status: rstatus, ...(lost_reason ? { lost_reason } : {}) }); toast.success("Renewal status updated"); load(); }
    catch { toast.error("Update failed"); }
  };

  const onStatusChange = (pol, v) => {
    if (v === "lost") { setLostFor(pol); setReason(""); setReasonNote(""); return; }
    updateStatus(pol, v);
  };

  const confirmLost = async () => {
    const final = reason === "Other" ? (reasonNote.trim() || "Other") : (reason || reasonNote.trim());
    if (!final) { toast.error("Please select or type a reason"); return; }
    await updateStatus(lostFor, "lost", final);
    setLostFor(null);
  };

  const tone = (days) => days == null ? "" : days <= 7 ? "text-rose-600 font-bold" : days <= 30 ? "text-amber-600 font-semibold" : "text-foreground";

  const columns = [
    { key: "policy_number", header: "Policy No", render: (r) => <span className="font-mono text-xs">{r.policy_number}</span> },
    { key: "customer_name", header: "Customer", render: (r) => <span className="font-semibold">{r.customer_name}</span> },
    { key: "insurer", header: "Insurer", render: (r) => <span className="text-sm">{r.insurer}</span> },
    { key: "premium", header: "Premium", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular font-semibold">{INR(r.premium)}</span> },
    { key: "expiry_date", header: "Expiry", render: (r) => <span className="text-sm">{fmtDate(r.expiry_date)}</span> },
    { key: "days", header: "Days Left", render: (r) => <span className={`tabular ${tone(r.days_to_expiry)}`}>{r.days_to_expiry != null ? `${r.days_to_expiry}d` : "—"}</span> },
    { key: "renewal_status", header: "Renewal Status", render: (r) => (
      hasPerm("renewals:edit") ? (
        <div onClick={(e) => e.stopPropagation()}>
          <Select value={RSTATUS.includes(r.renewal_status) ? r.renewal_status : "renewal_due"} onValueChange={(v) => onStatusChange(r, v)}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="renewal-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RSTATUS.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
          </Select>
          {r.renewal_status === "lost" && r.lost_reason && <div className="text-[10px] text-rose-600 mt-0.5">{r.lost_reason}</div>}
        </div>
      ) : <StatusBadge value={r.renewal_status} />
    ) },
    { key: "actions", header: "", render: (r) => (
      <ContactButtons name={r.customer_name} mobile={r.customer_mobile} product={r.product ? `${r.product} renewal` : "policy renewal"} testPrefix="renewal" />
    ) },
  ];

  return (
    <div>
      <PageHeader title="Renewal Management" subtitle="Policies approaching expiry — old policies never overwritten" icon={RefreshCw}
        actions={
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger className="w-44" data-testid="renewal-window-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{["7", "15", "30", "60", "90"].map((d) => <SelectItem key={d} value={d}>Next {d} days</SelectItem>)}</SelectContent>
          </Select>
        } />

      {forecast && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[["Due in 30 days", forecast.due_30], ["Due in 60 days", forecast.due_60], ["Due in 90 days", forecast.due_90]].map(([lbl, dd]) => (
            <Card key={lbl} className="p-4" data-testid="renewal-forecast-card">
              <div className="text-xs text-muted-foreground">{lbl}</div>
              <div className="text-2xl font-extrabold tabular mt-1">{dd?.count || 0}</div>
              <div className="text-[11px] text-muted-foreground tabular mt-1">Premium {INR(dd?.expected_premium)}</div>
              <div className="text-[11px] text-muted-foreground tabular">Comm. {INR(dd?.expected_commission)}</div>
            </Card>
          ))}
          <Card className="p-4">
            <div className="text-xs text-muted-foreground">Conversion</div>
            <div className="text-2xl font-extrabold tabular mt-1 text-emerald-600">{forecast.conversion_rate}%</div>
            <div className="text-[11px] text-muted-foreground mt-1">{forecast.renewals_completed} completed · {forecast.renewals_lost} lost</div>
          </Card>
        </div>
      )}

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        <DataTable columns={columns} rows={items} loading={false} rowTestId="renewal-pipeline-item" emptyText="No renewals due in this window" />}

      <Dialog open={!!lostFor} onOpenChange={(o) => !o && setLostFor(null)}>
        <DialogContent data-testid="renewal-lost-dialog">
          <DialogHeader><DialogTitle>Why was this renewal lost?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">{lostFor?.policy_number} · {lostFor?.customer_name}. This feeds the Lost Reason report.</p>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="lost-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
            <SelectContent>{LOST_REASONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {(reason === "Other" || !reason) && (
            <Input placeholder="Add details (optional if reason selected)" value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} data-testid="lost-reason-note" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostFor(null)}>Cancel</Button>
            <Button onClick={confirmLost} data-testid="lost-reason-confirm">Mark as Lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
